import { useEffect } from 'react';
import { useAppBadge } from '../hooks/useAppBadge';

/**
 * App Badge Manager Component
 * 
 * This component manages the app icon badge count across the entire application.
 * It should be placed high in the component tree, inside providers but wrapping content.
 * 
 * It automatically:
 * - Counts unread messages
 * - Counts pending time off requests (for admins)
 * - Counts pending payroll requests (for admins)
 * - Updates the app icon badge in real-time
 * - Subscribes to database changes for instant updates
 */
export function AppBadgeManager({ children }: { children: React.ReactNode }) {
  const { badgeCounts, isSupported, isPWA, refreshBadge } = useAppBadge();

  // Log badge service status on mount (helpful for debugging)
  useEffect(() => {
    console.log('📛 App Badge Manager initialized:', {
      isSupported,
      isPWA,
      userAgent: navigator.userAgent
    });

    if (!isSupported) {
      console.log('ℹ️ Badge API not supported in this browser');
      console.log('ℹ️ Supported browsers: Chrome/Edge (Android & Desktop), Safari (iOS 16.4+ PWA)');
    } else {
      console.log('✅ Badge API is supported!');
    }

    if (isSupported && !isPWA) {
      console.log('ℹ️ Badge API works best when app is installed as PWA');
    }

    // Make refreshBadge available globally for debugging
    // @ts-ignore
    window.refreshAppBadge = refreshBadge;
    console.log('📛 Global badge refresh available: window.refreshAppBadge()');
  }, [isSupported, isPWA, refreshBadge]);

  // Log badge count changes (for debugging)
  useEffect(() => {
    console.log('📛 Badge counts changed:', badgeCounts);
  }, [badgeCounts]);

  // This component doesn't render anything - it just manages the badge
  return <>{children}</>;
}

